import { Badge, Box } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { STATUS_COLORS, type StatusTone } from '@/theme/colors';

export type StatusScope = 'project' | 'object' | 'analysis';

/** Every client-facing status key mapped to one of the five tones (frontend-design.md §6). */
export const STATUS_TONE: Record<string, StatusTone> = {
  waiting_for_data: 'pending',
  uploading: 'active',
  checking_upload: 'active',
  checking_data: 'active',
  processing: 'active',
  data_needs_attention: 'failed',
  needs_attention: 'failed',
  data_ready: 'done',
  results_ready: 'done',
  results_partial: 'active',
  planned: 'muted',
  queued: 'pending',
  cancelled: 'muted',
  ready: 'done',
  nothing_outstanding: 'done',
  completed: 'muted',
  archived: 'muted',
  deleted: 'muted',
};

interface StatusBadgeBase {
  status: string;
  /** Replaces the translated label, e.g. a status with counts. */
  label?: string;
}

/** `results_partial` interpolates the counts, so a project badge cannot be rendered without them. */
export type StatusBadgeProps = StatusBadgeBase &
  (
    | { scope: 'project'; counts: { delivered: number; total: number } }
    | { scope: Exclude<StatusScope, 'project'>; counts?: undefined }
  );

/** Pill with a dot in the tone colour and a translated label; the one badge a client sees (P9). */
export function StatusBadge({ status, scope, label, counts }: StatusBadgeProps) {
  const { t } = useTranslation('projects');
  const c = STATUS_COLORS[STATUS_TONE[status] ?? 'muted'];
  return (
    <Badge
      variant="outline"
      fz={12}
      fw={600}
      px={10}
      // A state is never cut: a squeezed table cell would otherwise ellipsize the label to nothing.
      styles={{ root: { color: c.color, backgroundColor: c.bg, borderColor: c.border, height: 24, minWidth: 'max-content', flexShrink: 0 } }}
      leftSection={<Box w={6} h={6} bg={c.color} style={{ borderRadius: '50%' }} />}
    >
      {label ?? t(`status.${scope}.${status}`, { defaultValue: status, ...counts })}
    </Badge>
  );
}
